import { DestroyRef, inject, Injectable } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { FeatureFlags } from 'app/shared/services';
import { BehaviorSubject, Observable } from 'rxjs';
import { availableHubConfigs, availableHubConfigsYO } from '../../shared/constants/hub-configs.const';
import { HubConfig, RoomState } from '../../shared/types/hub-notifications.types';
import { ContextService } from './context.service';
import { NavigationService } from './navigation.service';

@Injectable({
  providedIn: 'root',
})
export class HubNotificationsService {
  protected readonly navigationService = inject(NavigationService);
  protected readonly contextService = inject(ContextService);

  protected activeHubConfigs: HubConfig[] = [];
  protected connections = new Map<string, HubConnection>();
  protected stateSubjects = new Map<string, BehaviorSubject<HubConnectionState>>();
  protected roomStates = new Map<string, RoomState>();

  private readonly initializationComplete = new BehaviorSubject<boolean>(false);
  public readonly initialized$ = this.initializationComplete.asObservable();

  public async initializeHubs(): Promise<void> {
    console.log('[HubNotifications] Starting hub initialization...');

    this.stopAllConnections();
    this.connections.clear();
    this.stateSubjects.clear();
    this.roomStates.clear();
    this.initializationComplete.next(false);

    const hubConfigs = this.contextService.isFeatureFlagEnabled(FeatureFlags.UseYardOpsApi)
      ? availableHubConfigsYO
      : availableHubConfigs;

    this.activeHubConfigs = hubConfigs.filter((config) => {
      const isVisible = this.navigationService.isMenuVisible(config.menuIdentifier);
      return isVisible;
    });

    try {
      await Promise.all(
        this.activeHubConfigs.map(async (cfg) => {
          await this.buildHubConnection(cfg);
          this.initializeRoomState(cfg.key);
        }),
      );

      this.initializationComplete.next(true);
    } catch (error) {
      console.error('[HubNotifications] Error during hub initialization:', error);
      this.initializationComplete.next(false);
      throw error;
    }
  }

  private initializeRoomState(key: string): void {
    this.roomStates.set(key, {
      rooms: new Set<string>(),
      lastJoinedAt: new Map<string, Date>(),
    });
  }

  private async buildHubConnection(cfg: HubConfig): Promise<void> {
    const { key, url } = cfg;
    const existingHub = this.connections.get(key);
    if (existingHub?.state === HubConnectionState.Connected) {
      return;
    }

    try {
      const hub = new HubConnectionBuilder()
        .withUrl(url, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .build();

      this.stateSubjects.set(key, new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected));

      await hub.start();
      console.log(`[HubNotifications] Successfully started hub for ${key}`);

      this.connections.set(key, hub);

      hub.onclose(() => {
        this.stateSubjects.get(key)?.next(HubConnectionState.Disconnected);
      });
    } catch (error) {
      console.error(`[HubNotifications] Failed to build connection for ${key}:`, error);
      throw error;
    }
  }

  public async startHubConnection<T = unknown>(
    key: string,
    destroyRef: DestroyRef,
    callBack: (entityId: string, payload: T) => void,
    args?: unknown | Observable<unknown>,
  ): Promise<void> {
    const config = this.activeHubConfigs.find((cfg) => cfg.key === key);
    const hub = this.connections.get(key);

    console.log(`[HubNotifications] Starting hub connection for ${key}. Hub state:`, hub?.state);

    if (!config || !hub || hub.state !== HubConnectionState.Connected) {
      try {
        await this.initializeHubs();
        // Retry the connection after initialization - safety mechanism
        return this.startHubConnection(key, destroyRef, callBack, args);
      } catch (error) {
        console.error(`[HubNotifications] Failed to initialize and retry hub connection for ${key}:`, error);
        throw new Error(`Hub ${key} initialization failed.`);
      }
    }

    if (config?.autoJoinRooms?.length) {
      try {
        if (args instanceof Observable) {
          args.subscribe((value) => {
            config?.autoJoinRooms?.forEach(async (room) => {
              await this.joinRoom(key, room, value);

              hub.on(config.inboundMethod, (data: T) => {
                callBack(value, data);
              });

              destroyRef.onDestroy(() => {
                hub.off(config.inboundMethod);
              });
            });
          });
        } else {
          const roomArgs = args ?? this.contextService.siteId.value;
          await Promise.all(
            config.autoJoinRooms.map(async (room) => {
              await this.joinRoom(key, room, roomArgs);
              hub.on(config.inboundMethod, (data: T) => {
                callBack(roomArgs as string, data);
              });

              destroyRef.onDestroy(() => {
                //TODO: leave room (trigger backend call)
                hub.off(config.inboundMethod);
              });
            }),
          );
        }
      } catch (err) {
        console.error(`[${key}Hub] Failed to join rooms:`, err);
        throw err;
      }
    }
  }

  public async joinRoom(key: string, room: string, ...args: unknown[]): Promise<void> {
    const hub = this.connections.get(key);
    const roomState = this.roomStates.get(key);

    if (!hub) {
      const error = `No hub configured under key="${key}". Available keys: ${Array.from(this.connections.keys()).join(', ')}`;
      console.error(error);
      throw new Error(error);
    }

    if (hub.state !== HubConnectionState.Connected) {
      const error = `Hub "${key}" is not connected (current state: ${hub.state}).`;
      console.error(error);
      throw new Error(error);
    }

    if (!args.length) {
      args = [this.contextService.siteId.value];
    }

    try {
      await hub.invoke(room, ...args);
      roomState?.rooms.add(room);
      roomState?.lastJoinedAt.set(room, new Date());
      console.log(`[${key}Hub] Joined room: ${room}`);
    } catch (err) {
      console.error(`[${key}Hub] Failed to join room ${room}:`, err);
      throw err;
    }
  }

  public async stopConnection(key: string): Promise<void> {
    const hub = this.connections.get(key);
    const roomState = this.roomStates.get(key);

    if (!hub) {
      throw new Error(`No hub configured under key="${key}".`);
    }

    if (hub.state === HubConnectionState.Connected) {
      if (roomState) {
        roomState.rooms.clear();
        roomState.lastJoinedAt.clear();
      }

      await hub.stop();
      this.stateSubjects.get(key)?.next(HubConnectionState.Disconnected);
    }
  }

  public async stopAllConnections(): Promise<void> {
    for (const key of this.connections.keys()) {
      await this.stopConnection(key);
    }
  }
}
